<template>
  <div
    v-if="pages.length > 1"
    class="btn-group"
  >
    <button
      class="btn"
      :class="[mini ? 'pagination-btn' : 'btn-default']"
      type="button"
      :style="{ cursor: canLeft ? 'pointer' : 'default' }"
      :disabled="!canLeft"
      @click.prevent="left()"
      v-text="mini ? '<' : '«'"
    />
    <!--
        Вызывается при изменении значения.

        @event input
        @property {Number} value Номер страницы.
      -->
    <template v-if="!mini">
      <template v-for="(item, index) of pages">
        <button
          v-if="item.type === 'page'"
          :key="`page-${item.number}`"
          class="btn"
          type="button"
          :class="{ 'btn-warning': item.number === value, 'btn-default': item.number !== value }"
          @click.prevent="$emit('input', item.number)"
          v-text="item.number"
        />
        <span
          v-else-if="item.type === 'ellipsis'"
          :key="`ellipsis-${index}`"
          class="btn btn-default"
          style="cursor: default;"
          v-text="'...'"
        />
      </template>
    </template>
    <button
      v-else
      class="btn pagination-page"
      v-text="value"
    />
    <button
      class="btn"
      :class="[mini ? 'pagination-btn' : 'btn-default']"
      type="button"
      :style="{ cursor: canRight ? 'pointer' : 'default' }"
      :disabled="!canRight"
      @click.prevent="right()"
      v-text="mini ? '>' : '»'"
    />
  </div>
</template>

<script>
/**
 * Пагинация.
 */
export default {
  name: 'CPagination',
  props: {
    /**
     * Текущее значение.
     *
     * @model
     */
    value: {
      type: Number,
      required: true
    },
    /**
     * Количество элементов на одной странице пагинации.
     */
    perPage: {
      type: Number,
      default: 10
    },
    /**
     * Общее количество элементов.
     */
    total: {
      type: Number,
      required: true
    },
    /**
     * Минималистичное отображение без непосредственного выбора страницы.
     */
    mini: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /**
     * Общее количество страниц пагинации.
     *
     * @returns {Number}
     */
    totalPages () {
      return Math.ceil(this.total / this.perPage)
    },
    /**
     * Возвращает номера страниц пагинации в зависимости от общего количества элементов и количества элементов
     * на одной странице пагинации. При большом количестве страниц показывает разрывы.
     *
     * @returns {[]}
     */
    pages () {
      const totalPages = this.totalPages
      const currentPage = this.value
      const pages = []

      // Если страниц 7 или меньше, показываем все
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push({ type: 'page', number: i })
        }
        return pages
      }

      // Всегда показываем первую страницу
      pages.push({ type: 'page', number: 1 })

      // Определяем диапазон страниц вокруг текущей
      const showAround = 2 // Количество страниц с каждой стороны от текущей
      const start = Math.max(2, currentPage - showAround)
      const end = Math.min(totalPages - 1, currentPage + showAround)

      // Если между первой страницей и началом диапазона есть разрыв
      if (start > 2) {
        pages.push({ type: 'ellipsis' })
      }

      // Добавляем страницы вокруг текущей
      for (let i = start; i <= end; i++) {
        // Пропускаем первую и последнюю, так как они уже добавлены/будут добавлены
        if (i !== 1 && i !== totalPages) {
          pages.push({ type: 'page', number: i })
        }
      }

      // Если между концом диапазона и последней страницей есть разрыв
      if (end < totalPages - 1) {
        pages.push({ type: 'ellipsis' })
      }

      // Всегда показываем последнюю страницу (если она не первая)
      if (totalPages > 1) {
        pages.push({ type: 'page', number: totalPages })
      }

      return pages
    },
    /**
     * Можно ли переместиться на одну страницу влево.
     *
     * @returns {boolean}
     */
    canLeft () {
      return this.value > 1
    },
    /**
     * Можно ли переместиться на одну страницу вправо.
     *
     * @returns {boolean}
     */
    canRight () {
      return this.value < this.totalPages
    }
  },
  methods: {
    /**
     * Переключается на страницу влево (если возможно).
     */
    left () {
      if (this.canLeft) {
        this.$emit('input', this.value - 1)
      }
    },
    /**
     * Переключается на страницу вправо (если возможно).
     */
    right () {
      if (this.canRight) {
        this.$emit('input', this.value + 1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./resources/css/_variables.scss";

.pagination-btn {
  background-color: unset;
}

.pagination-page {
  cursor: default;
  background-color: white;
  border-radius: $borderRadiusMedium !important;
}
</style>
